'use client'

import * as React from 'react'
import { ChevronDown, LogOut, User as UserIcon } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './dropdown-menu'
import { Spinner } from './spinner'
import { cn } from '../../utils'

export interface ProfileDropdownUser {
  name?: string | null
  email?: string | null
  initials?: string | null
}

export interface ProfileDropdownProps {
  user: ProfileDropdownUser | null
  /** Loading indicator next to the name (e.g. while persisting a setting). */
  busy?: boolean
  /** Profile menu item — fires when clicked. Omit to hide. */
  onProfile?: () => void
  /** Logout menu item — fires when clicked. Omit to hide. */
  onLogout?: () => void
  /** Custom label for the profile item. Defaults to "Profile". */
  profileLabel?: string
  /** Custom label for the logout item. Defaults to "Logout". */
  logoutLabel?: string
  /**
   * Optional extra menu content rendered between the profile and logout
   * items — typically a sub-menu (e.g. language switcher) provided by the
   * consuming app. Wrap with whatever DropdownMenuSub etc. you need.
   */
  extra?: React.ReactNode
  className?: string
  /** Override class on the popover content (e.g. to widen the menu). */
  contentClassName?: string
  /** Optional avatar override. Defaults to initials in a circle. */
  renderAvatar?: (user: ProfileDropdownUser) => React.ReactNode
}

function defaultInitials(name?: string | null, fallback?: string | null) {
  if (name) {
    return name.split(' ').map((w) => w[0]).filter(Boolean).join('').slice(0, 2).toUpperCase()
  }
  return (fallback ?? '?').slice(0, 2).toUpperCase()
}

function DefaultAvatar({ user }: { user: ProfileDropdownUser }) {
  const initials = user.initials ?? defaultInitials(user.name, user.email)
  return (
    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary shrink-0">
      {initials}
    </div>
  )
}

/**
 * Topbar profile widget: avatar + name + dropdown with profile / logout.
 *
 * Pure presentation — the caller passes the user object and event handlers.
 * Apps that need extra menu items (e.g. a language switcher) render them
 * via the `extra` slot, which is dropped between profile and logout.
 */
export function ProfileDropdown({
  user,
  busy = false,
  onProfile,
  onLogout,
  profileLabel = 'Profile',
  logoutLabel = 'Logout',
  extra,
  className,
  contentClassName,
  renderAvatar,
}: ProfileDropdownProps) {
  if (!user) return null

  const display =
    user.name?.trim() || user.email?.trim() || profileLabel || 'User'

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={busy}
          className={cn(
            'flex items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors',
            'hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            'disabled:opacity-50',
            className,
          )}
        >
          {busy ? <Spinner size="sm" /> : (renderAvatar ? renderAvatar(user) : <DefaultAvatar user={user} />)}
          <div className="hidden lg:flex flex-col items-start leading-tight max-w-[160px] min-w-0">
            <span className="text-sm font-medium text-foreground truncate">{display}</span>
            {user.email && user.email !== display && (
              <span className="text-xs text-muted-foreground truncate">{user.email}</span>
            )}
          </div>
          <ChevronDown className="w-4 h-4 text-muted-foreground hidden lg:block" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className={cn('w-52', contentClassName)}>
        {onProfile && (
          <DropdownMenuItem onClick={onProfile} className="gap-2">
            <UserIcon className="w-4 h-4 text-muted-foreground" />
            {profileLabel}
          </DropdownMenuItem>
        )}
        {extra && (
          <>
            {onProfile && <DropdownMenuSeparator />}
            {extra}
          </>
        )}
        {onLogout && (
          <>
            {(onProfile || extra) && <DropdownMenuSeparator />}
            <DropdownMenuItem
              onClick={onLogout}
              className="gap-2 text-destructive focus:text-destructive"
            >
              <LogOut className="w-4 h-4" />
              {logoutLabel}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
